import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import * as PR from "../../../prime-react/index";
import "../AlertTags/AlertTags.scss";
import { analystChartTitles, areDateRangesEqual, defaultDateRange, pagecount, roleIds } from "../../../utils/reuse";
import { getAlertsCount, getGroups } from "../../../services/api";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "../../../context/ToastContext";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { download, generateCsv, mkConfig } from "export-to-csv";
import { resetReportsDateRange, storeReportsDateRange } from "../../../store/filter.slice";

const ReportsSentRegulators = ({ regulatorsData, sgbData, selectedDates, isDetailScreen, exportVisible, setExportModalHandler, offsetvalue, pageValue, setPaginationHandler, getResponseHandler}) => {
    const graphType = 'rspr';
    const { showToast } = useToast();
    const [selectedRegulator, setSelectedRegulator] = useState(null);
    const [dates, setDates] = useState(selectedDates);
    const sessionData = useSelector((state) => state.auth.sessionData);
    const headers = useMemo(() => { return { sessionid: sessionData.sessionId, }; }, [sessionData.sessionId]);
    const isExport = useSelector(state => state.auth?.exportPermission);
    const menuRight = useRef(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const pageRecords = isDetailScreen ? pagecount : 10;
    const limit = pageRecords + 1;
    const [offset, setoffset] = useState(0);
    const [page, setPage] = useState(1);
    const [regulators, setRegulators] = useState([])
    const [regulatorsSGB, setRegulatorsSGB] = useState([])
    const [governingBodies, setGoverningBodies] = useState([]);
    const [reportsData, setReportsData] = useState([]);
    const [sortField, setSortField] = useState('report_type');
    const [sortOrder, setSortOrder] = useState(-1);
    const tooltipRef = useRef(null);
    useEffect(() => {
        if (!isDetailScreen) {
            const fetchAndSetGroups = (roleId, setter) => {
                getGroups(roleId, headers, dispatch, (response) => {
                    if (response.result === "SUCCESS") {
                        const activeGroups = (response.data || []).filter(r => r.status === "active");
                        setter(activeGroups.map(r => ({ label: r.name, value: r.id })));
                    } else {
                        const error = response.error;
                        showToast(error?.severity, error?.errorMsg || error?.summary);
                    }
                });
            };
            // Fetch multiple roles
            fetchAndSetGroups("3,4", setRegulatorsSGB);
            fetchAndSetGroups(roleIds?.regulator, setRegulators);
            fetchAndSetGroups(roleIds?.governingbody, setGoverningBodies);
  }
}, [dispatch, headers, isDetailScreen, showToast]);
    const formatDates = (dates) => {
        if (!dates || dates.length === 0) return {};
        const startDate = moment(dates[0]).format('YYYY-MM-DD');
        const endDate = dates[1] ? moment(dates[1]).format('YYYY-MM-DD') : startDate;
        return { startDate, endDate };
    };
     const getDateForFileName = (dates) => {
        let currDate = `_${moment(dates[0]).format('DD_MMM_YYYY')}${moment(dates[1]).isValid() ? `_to_${moment(dates[1]).format('DD_MMM_YYYY')}` : ''}`;
        return currDate;
    }
    const downloadHandler = useCallback((reportsData, dateValue) => {
        const reports = reportsData.map(r => {
            const receivers = r.receivers?.length ? r.receivers : []
            const report = {
                reportID: r.reportID,
                reportTitle: r.title,
                reportType: r.reportTypeDisplay,
                alertId: r.alertID,
                sportID: r.sportID,
                sportName: r.sportName,
                regulators: receivers.filter(r => r.role === "regulator").map(r => r.name).join(';'),
                sportGoverningBody: receivers.filter(r => r.role === "governingbody").map(r => r.name).join(';')
            }
            return report;
        })
        const currDate = getDateForFileName(dateValue);
        const downloadedFilename = analystChartTitles[graphType]?.fileName + currDate;
        const csvConfig = mkConfig({ useKeysAsHeaders: true, filename: downloadedFilename });
        const csv = generateCsv(csvConfig)(reports);
        download(csvConfig)(csv)
    }, [])
    const fetchData = useCallback((selectedOptions, dateValue, isExport = false) => {
        const dateData = formatDates(dateValue);
        const obj = {
            type: graphType,
            offset,
            limit,
            ...dateData,
            sort_by: sortField,
            sort_order: sortOrder === 1 ? 'asc' : 'desc',
        };
        if(isExport){
           obj.export = true; 
        }
        if (selectedOptions.length > 0) {
            obj.receivers = selectedOptions?.toString();
        }
        getAlertsCount(obj, headers, dispatch, (response) => {
            if (response.result === 'SUCCESS') {
                const responseData = response.data;
                let reportsSentPerReceiver = responseData?.['reportsSentPerReceiver'];
                if (reportsSentPerReceiver?.length) {
                      reportsSentPerReceiver = reportsSentPerReceiver.map(r => {
                        r.reportTypeDisplay = r.reportType === 'suspicious betting' ? 'Suspicious Betting' : r.reportType === 'summary' ? 'Summary' : 'Other';
                        r.alertID = r.alertID ? r.alertID : '-'
                        const receivers =  r.receivers.map(r => r.name).join('; ');
                        r.regulatorsSGB = receivers;
                        r.regulatorsSGBShort = receivers.length > 50 ? receivers.slice(0, 50)+ '...' : receivers;
                        return r;
                    })
                }
                if(isExport){
                    downloadHandler(reportsSentPerReceiver, dateValue);
                } else {
                    setReportsData(reportsSentPerReceiver);
                }
            }
        });
    }, [offset, limit, sortField, sortOrder, headers, dispatch, downloadHandler]);
    useEffect(() => {
        if (getResponseHandler) {
            getResponseHandler(reportsData); // pass the data to details screen
        }
    }, [getResponseHandler, reportsData]);

    useEffect(() => {
        if (!isDetailScreen) return;
        const selectedOptions = [...(regulatorsData || []), ...(sgbData || [])];
        const dateValue = selectedDates;
        if (dateValue) {
            fetchData(selectedOptions, dateValue);
        }
    }, [isDetailScreen, regulatorsData, sgbData, selectedDates, fetchData, offsetvalue]);
    useEffect(() => {
        if (isDetailScreen) {
            setoffset(offsetvalue);
            setPage(pageValue);
        }
    }, [isDetailScreen, offsetvalue, pageValue]);

    useEffect(() => {
        if (isDetailScreen) return;
        const selectedOptions = selectedRegulator ? [selectedRegulator] : []
        const dateValue = dates;
        if (dateValue) {
            fetchData(selectedOptions, dateValue);
        }
    }, [isDetailScreen, selectedRegulator, dates, fetchData]);
    useEffect(() => {
        if (isDetailScreen && exportVisible) {
            const selectedOptions = [...(regulatorsData || []), ...(sgbData || [])];
            fetchData(selectedOptions, selectedDates, true);
            setExportModalHandler(false);
        }
    }, [isDetailScreen, exportVisible, downloadHandler, setExportModalHandler, selectedDates, regulatorsData, sgbData, fetchData]);
    useEffect(() => {
        tooltipRef.current?.updateTargetEvents?.(); // Rebind events
    }, [reportsData]); 
    const items = [
        {
            label: 'View',
            icon: 'pi pi-eye',
            disabled: !reportsData?.length,
            command: () => goToAnalyticsDetails(graphType)
        },
        {
            label: 'Export',
            icon: 'pi pi-upload',
            disabled: !isExport ? true : !reportsData?.length,
            command: () => fetchData(selectedRegulator ? [selectedRegulator] : [], dates, true)
        }
    ];
    const paginationHandler = (page, val) => {
        setPage(page + val);
        setoffset(((page + val) - 1) * pageRecords)
        if(isDetailScreen){
            setPaginationHandler(((page + val) - 1) * pageRecords, page + val)
        }
    }

    const PaginationTemplate = () => {
        return <div className="p-paginator p-component p-paginator-bottom">
            <span className="current-page">Page {page}</span>
            <PR.Button className="pagination-buttons" disabled={!(page - 1)} onClick={paginationHandler?.bind(null, page, -1)} > Previous </PR.Button>
            <PR.Button className="pagination-buttons" disabled={reportsData?.length <= (pageRecords)} onClick={paginationHandler?.bind(null, page, 1)} > Next </PR.Button>
        </div>
    };
    const goToAnalyticsDetails = (type) => {
        const selReg = selectedRegulator ? [selectedRegulator]:[];
        const regulatorsData = regulators.filter(r => selReg.includes(r.value)).map(r => r.value)
        const sgbData = governingBodies.filter(r => selReg.includes(r.value)).map(r => r.value)
        navigate("/my-analytics-details?type=" + type, { state: { graphType, selectedDates: dates, selectedRegulator: regulatorsData, selectedSGB: sgbData, regulators, governingBodies } });
    }
    const action = (rowData) => {
        return (
            <div className="flex flex-wrap">
                <PR.Button label="View Report" type='submit' severity="secondary" outlined className="view-report-button "  onClick={() => navigate(`/report-detail?id=${rowData.reportID}`)} />
            </div>
        );
    };
    const regulatorsSGBBodyTemplate = (rowData) => {
        const regulators = rowData.receivers?.length
            ? rowData.receivers.filter(r => r.role === 'regulator').map((r) => r.name)
            : [];
        const governingBodies = rowData.receivers?.length
            ? rowData.receivers.filter(r => r.role === 'governingbody').map((r) => r.name)
            : [];
        let tooltipContent = '';
        if (regulators.length) {
            tooltipContent += `Regulators:\n ${regulators.join('\n ')} \n \n`;
        } 
        if (governingBodies.length) {
            tooltipContent += `Sports Governing Bodies:\n ${governingBodies.join('\n ')}`;
        }
         return (
            <span
                className="regulator-tooltip"
                data-pr-tooltip={tooltipContent}
                data-pr-position="bottom"
                data-pr-classname="regulator-tooltip-modal"
            >
                {rowData.regulatorsSGBShort}
            </span>
        );
    };
    useEffect(() => {
      setDates(selectedDates);
    }, [selectedDates]);
    const calenderHandler = (value) => {
        dispatch(storeReportsDateRange(value))
    }
    const resetDateHandler = () => {
        dispatch(resetReportsDateRange())
    }
    const isDefaultDateRange = areDateRangesEqual(dates, defaultDateRange);

    return (
        <div className="info-box">
            {!isDetailScreen &&
                <>
                    <div className="flex align-items-start justify-content-between gap-2">
                        <div className='pr-0'>
                            <h4>{analystChartTitles[graphType]?.title}</h4>
                            <p>{analystChartTitles[graphType]?.description}</p>
                        </div>
                        <div className='date custom-clear-calendar'>
                            <PR.Calendar
                                value={dates}
                                onChange={(e) => calenderHandler(e.value)}
                                selectionMode="range"
                                iconPos="left"
                                readOnlyInput
                                showIcon
                                className='date-calendar'
                                placeholder='Date'
                                dateFormat="dd/mm/yy"
                                maxDate={new Date()}
                            />
                            {!isDefaultDateRange &&
                                <div className='custom-clear'>
                                    <i className="pi pi-times text-sm" onClick={() => resetDateHandler()}></i>
                                </div>
                            }
                        </div>
                    </div>

                    <div className="flex align-items-center justify-content-between mt-1">
                        <PR.Dropdown
                            filter
                            value={selectedRegulator} onChange={(e) => {setoffset(0);setPage(1);setSelectedRegulator(e.value)}}
                            options={regulatorsSGB}
                            placeholder="Select Regulator/SGB"
                            className="multi-select-dropdown"
                            resetFilterOnHide
                            showClear
                        />
                        <div>
                            <PR.Menu model={items} popup ref={menuRight} id="popup_menu_right" className="export-popup" popupAlignment="right" />
                            <PR.Button icon="pi pi-ellipsis-v" className="export-button" onClick={(event) => menuRight.current.toggle(event)} aria-controls="popup_menu_right" aria-haspopup />
                        </div>
                    </div>
                </>
            }
            <div>
                <PR.Tooltip ref={tooltipRef} target=".regulator-tooltip" position="bottom"/>
                <PR.DataTable
                    value={reportsData?.slice(0, pageRecords)}
                    className="data-table"
                    footer={<PaginationTemplate></PaginationTemplate>}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={({ sortField: F, sortOrder: v }) => {setoffset(0);setPage(1);setSortField(F); setSortOrder(v); }}
                >
                    {isDetailScreen && <PR.Column  header="Regulator/SGB"  body={regulatorsSGBBodyTemplate} />}
                    <PR.Column field='title' header="Report " />
                    <PR.Column field='reportTypeDisplay' header="Report Type" sortable sortField="report_type"/>
                    <PR.Column field='alertID' header="Alert-ID" />
                    <PR.Column field='sportName' header="Sport" sortable sortField="sport"/>
                    {isDetailScreen && <PR.Column body={action} header="Action" />}
                   
                </PR.DataTable>
            </div>
        </div>
    )
}

export default ReportsSentRegulators